import { ReactNode, useMemo, useState } from 'react';
import OpsDetailModal from './OpsDetailModal';
import { won } from './tabData';

/**
 * 「왜 이 금액인가」 — 화면의 금액을 누르면 뜨는 산출 근거 팝업.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 만들었나 (2026-09-01 QA31, 대표 지시)
 * ─────────────────────────────────────────────────────────────
 * 헤더의 「지켜야 할 매출 2.7억」은 이 기능에서 가장 무거운 숫자인데, 그 근거는
 * 마우스오버 툴팁 몇 줄이 전부였습니다. 툴팁은 **정의**만 말하고 **누가 얼마씩
 * 들어갔는지**는 말하지 못합니다. 원장님이 "이 2.7억이 어디서 나왔냐"고 물으면
 * 화면이 답할 수 없었고, 답 못 하는 금액은 결국 안 믿게 됩니다.
 *
 * 그래서 금액을 **누를 수 있게** 하고, 누르면 구성 내역을 한 줄씩 폅니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 이 파일이 지켜야 하는 것 — **여기서 계산하지 않는다**
 * ─────────────────────────────────────────────────────────────
 * 이 컴포넌트는 숫자를 **받아서 늘어놓기만** 합니다. 합계도 부르는 쪽이 화면에
 * 이미 그린 그 변수를 그대로 넘깁니다 — 여기서 다시 더하면 팝업이 헤더와
 * 다른 값을 말할 수 있고, 그 순간 팝업은 신뢰를 주는 대신 깎아먹습니다.
 *
 * 줄마다 붙는 「잔여 15회 × 55,000원」도 여기서 조립하지 않습니다.
 * 계산기(`memberValue.computeMemberValue`)가 금액과 **같은 자리에서** 만든
 * `prepaidBasis` / `expectedBasis` 를 그대로 받습니다. 같은 식이 두 곳에 있으면
 * 반드시 갈립니다 (`memberValue.ts` 의 `prepaidBasis` 주석).
 *
 * ⚠️ 그래서 `rows` 의 합이 `total` 과 같아야 하는 책임은 **부르는 쪽**에 있습니다.
 *    합계를 만든 목록과 줄을 만든 목록이 같아야 합니다. 테스트가 이것을 잠급니다
 *    (`briefingValueScreen.test.tsx` 의 「팝업 내역 합계 = 헤더 금액」).
 *
 * 층위는 2400 입니다 — 회원 상세(2300) **안에서도** 열리기 때문입니다
 * (`OpsDetailModal` 의 층위표).
 */

export type ValueBreakdownRow = {
    /** React key. 회원 카드면 cardKey, 강사면 coachId */
    key: string;
    /** 왼쪽 굵은 글씨 — 회원 이름 · 강사 이름 */
    name: string;
    /** 이름 옆 회색 글씨 — 「김코치 강사 · 20회권」. 없으면 생략 */
    sub?: string | null;
    /**
     * 근거 한 조각 — 「잔여 15회 × 55,000원」.
     * **계산기가 만든 문자열을 그대로 넘깁니다.** 없으면 금액만 적습니다.
     */
    basis?: string | null;
    /** 이 줄의 금액(원). `null` 이면 「산정 불가」로 적고 합계에 안 넣습니다 */
    amount: number | null;
    /** 이 줄만의 단서 — 「순번을 확정할 수 없어 3번째 요율 적용」 */
    note?: string | null;
};

type Props = {
    /** 창 제목 — 화면에 적힌 금액 이름 그대로. 「지켜야 할 매출」 */
    title: string;
    /** 정의 한 줄. 이 금액이 무엇인지 */
    definition: string;
    /** 무엇을 세고 있는지 — 「9월 1일 올라온 5건 기준」. 없으면 생략 */
    scopeNote?: string | null;
    /** 구성 내역. 합계를 만든 **그 목록**이어야 합니다 */
    rows: ValueBreakdownRow[];
    /**
     * 합계 — 화면이 이미 그린 그 숫자를 그대로 넘깁니다.
     * 여기서 다시 더하지 않습니다 (파일 머리말 참고).
     */
    total: number;
    /** 금액을 못 낸 건 — 「산정 불가 3건(단가 미등록)」 */
    unavailable?: { count: number; reason: string } | null;
    /** 정책 문구 — 「이탈 위험·주의는 재등록을 기대하지 않아 0입니다」 */
    policyNotes?: string[];
    /**
     * 한 건짜리 근거인가 — 카드 배지에서 열 때.
     * 합계 줄과 「구성 내역 N건」 머리말을 빼고 한 줄만 보여줍니다.
     */
    single?: boolean;
    onClose: () => void;
};

/** 목록이 길어도 창이 안 넘치게 — 안에서 스크롤한다 */
const LIST_MAX_HEIGHT = 360;

const Section = ({ children, style }: { children: ReactNode; style?: React.CSSProperties }) => (
    <div
        className="rounded-[10px]"
        style={{ background: '#FFFFFF', border: '1px solid #E3EAF3', padding: '13px 15px', ...style }}
    >
        {children}
    </div>
);

const ValueBreakdownModal = ({
    title,
    definition,
    scopeNote,
    rows,
    total,
    unavailable,
    policyNotes = [],
    single = false,
    onClose,
}: Props) => {
    /*
     * 큰 금액이 위로. 정렬은 **보기 순서일 뿐**이라 합계에 영향을 주지 않는다.
     * 금액을 못 낸 줄(null)은 맨 아래로 — 합계에 안 들어간 줄이 위에 섞여 있으면
     * 원장님이 눈으로 더할 때 안 맞는다.
     */
    const sorted = useMemo(
        () =>
            [...rows].sort((a, b) => {
                if (a.amount == null && b.amount == null) return 0;
                if (a.amount == null) return 1;
                if (b.amount == null) return -1;
                return b.amount - a.amount;
            }),
        [rows]
    );

    return (
        <OpsDetailModal
            label={`${title} 산출 근거`}
            closeLabel={`${title} 산출 근거 닫기`}
            width={560}
            /* 회원 상세(2300) 안에서도 열린다 — 아래에 두면 막 뒤로 깔린다 */
            zIndex={2400}
            onClose={onClose}
            header={
                <div className="flex flex-col">
                    <span style={{ fontSize: 12.5, color: '#9DB4D8' }}>왜 이 금액인가</span>
                    <span
                        style={{
                            fontSize: 20,
                            fontWeight: 800,
                            color: '#FFFFFF',
                            marginTop: 5,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        {title}
                    </span>
                    {scopeNote && (
                        <span style={{ fontSize: 12, color: '#8FA5C4', marginTop: 4 }}>
                            {scopeNote}
                        </span>
                    )}
                </div>
            }
        >
            <div className="flex flex-col" style={{ gap: 10 }}>
                {/* 정의 — 이 금액이 무엇인가. 툴팁에만 있던 문장이 여기로 왔다 */}
                <Section style={{ borderLeft: '3px solid #3D73DD' }}>
                    <span
                        style={{
                            fontSize: 13.5,
                            color: '#2C3A4B',
                            lineHeight: '20px',
                            wordBreak: 'keep-all',
                        }}
                    >
                        {definition}
                    </span>
                </Section>

                {/* 구성 내역 */}
                <Section style={{ padding: '13px 0 4px' }}>
                    {!single && (
                        <div
                            className="flex items-baseline justify-between"
                            style={{ padding: '0 15px 9px', gap: 8 }}
                        >
                            <span style={{ fontSize: 13, fontWeight: 800, color: '#16202E' }}>
                                구성 내역
                            </span>
                            <span style={{ fontSize: 12, color: '#93A0AE', whiteSpace: 'nowrap' }}>
                                {sorted.length}건
                            </span>
                        </div>
                    )}

                    {sorted.length === 0 ? (
                        <div style={{ padding: '4px 15px 12px' }}>
                            <span style={{ fontSize: 13, color: '#7C8794' }}>
                                금액이 잡힌 건이 없습니다.
                            </span>
                        </div>
                    ) : (
                        <div
                            style={{
                                maxHeight: single ? undefined : LIST_MAX_HEIGHT,
                                overflowY: single ? undefined : 'auto',
                            }}
                        >
                            {sorted.map((r, i) => (
                                <div
                                    key={r.key}
                                    className="flex items-start justify-between"
                                    style={{
                                        gap: 12,
                                        padding: '9px 15px',
                                        borderTop: i === 0 ? 'none' : '1px solid #F1F4F9',
                                    }}
                                >
                                    <div
                                        className="flex flex-col"
                                        style={{ minWidth: 0, gap: 2 }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 13.5,
                                                fontWeight: 700,
                                                color: '#16202E',
                                                wordBreak: 'keep-all',
                                            }}
                                        >
                                            {r.name}
                                            {r.sub && (
                                                <span
                                                    style={{
                                                        fontSize: 12.5,
                                                        fontWeight: 400,
                                                        color: '#93A0AE',
                                                        marginLeft: 6,
                                                    }}
                                                >
                                                    {r.sub}
                                                </span>
                                            )}
                                        </span>
                                        {r.basis && (
                                            <span
                                                style={{
                                                    fontSize: 12.5,
                                                    color: '#5B6B7F',
                                                    wordBreak: 'keep-all',
                                                }}
                                            >
                                                {r.basis}
                                            </span>
                                        )}
                                        {r.note && (
                                            <span
                                                style={{
                                                    fontSize: 11.5,
                                                    color: '#A7B0BC',
                                                    wordBreak: 'keep-all',
                                                }}
                                            >
                                                {r.note}
                                            </span>
                                        )}
                                    </div>
                                    {/*
                                      금액을 못 낸 줄은 0원이라고 적지 않는다.
                                      0원은 「다 썼다」이고 여기는 「모른다」다 —
                                      화면 다른 자리와 같은 규칙(memberValue.priced).
                                    */}
                                    <span
                                        style={{
                                            fontSize: r.amount == null ? 12.5 : 14,
                                            fontWeight: r.amount == null ? 600 : 800,
                                            color: r.amount == null ? '#A7B0BC' : '#16202E',
                                            whiteSpace: 'nowrap',
                                            flex: 'none',
                                            letterSpacing: '-0.02em',
                                        }}
                                    >
                                        {r.amount == null ? '산정 불가' : `${won(r.amount)}원`}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </Section>

                {/* 합계 — 화면이 그린 그 숫자를 그대로 적는다 */}
                {!single && (
                    <Section
                        style={{
                            background: '#F1F5FC',
                            border: '1px solid #D3E0F5',
                        }}
                    >
                        <div className="flex items-baseline justify-between" style={{ gap: 12 }}>
                            <span style={{ fontSize: 13.5, fontWeight: 800, color: '#2C4C8C' }}>
                                합계
                            </span>
                            <span
                                style={{
                                    fontSize: 19,
                                    fontWeight: 800,
                                    color: '#2C4C8C',
                                    letterSpacing: '-0.03em',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {won(total)}원
                            </span>
                        </div>
                    </Section>
                )}

                {/*
                  합계에 안 들어간 건. 안 밝히면 원장님은 이 합계를 「전부」로 읽는데
                  사실은 일부만 센 것이다 — 화면의 다른 자리와 같은 규칙이다.
                */}
                {unavailable && unavailable.count > 0 && (
                    <Section style={{ background: '#F7F9FC', border: '1px solid #E9EDF4' }}>
                        <span
                            style={{
                                fontSize: 12.5,
                                color: '#5B6B7F',
                                lineHeight: '19px',
                                wordBreak: 'keep-all',
                            }}
                        >
                            ※ 산정 불가 {unavailable.count}건은 이 합계에서 빠져 있습니다 —{' '}
                            {unavailable.reason}
                        </span>
                    </Section>
                )}

                {policyNotes.length > 0 && (
                    <div className="flex flex-col" style={{ gap: 5, padding: '2px 2px 4px' }}>
                        {policyNotes.map((note) => (
                            <span
                                key={note}
                                style={{
                                    fontSize: 12.5,
                                    color: '#7C8794',
                                    lineHeight: '19px',
                                    wordBreak: 'keep-all',
                                }}
                            >
                                ※ {note}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </OpsDetailModal>
    );
};

export default ValueBreakdownModal;

/* ═══════════════════════════════════════════════════════════
   금액을 누를 수 있게 만드는 껍데기
   ═══════════════════════════════════════════════════════════ */

/**
 * 금액 버튼.
 *
 * ⚠️ **보이는 것을 바꾸지 않습니다.** 대표 지시가 「시각은 절제, 지금 레이아웃
 *    유지」였습니다. 그래서 `<span>` 을 `<button>` 으로 바꾸되 브라우저 기본
 *    단추 모양(배경·테두리·여백·글꼴)을 전부 끄고, 누를 수 있다는 신호는
 *    **커서와 점선 밑줄**로만 줍니다.
 *
 * ⚠️ 이미 테두리가 있는 배지(카드의 금액 태그)는 `underline={false}` 로 부릅니다 —
 *    칩 안에 밑줄까지 있으면 지저분하고, 칩 자체가 이미 눌러 보입니다.
 */
export const ValueTrigger = ({
    label,
    onClick,
    underline = true,
    stopPropagation = false,
    className,
    style,
    children,
}: {
    /** 스크린리더가 읽을 이름 — 「지켜야 할 매출 산출 근거 보기」 */
    label: string;
    onClick: () => void;
    underline?: boolean;
    /**
     * 누른 것이 **위로 번지지 않게** 막는다.
     *
     * 단계 카드처럼 상자 전체가 눌리는 자리 안에 있을 때 필요하다 —
     * 안 막으면 금액을 눌렀는데 단계까지 함께 바뀐다.
     */
    stopPropagation?: boolean;
    className?: string;
    style?: React.CSSProperties;
    children: ReactNode;
}) => {
    const [hover, setHover] = useState(false);
    return (
        <button
            type="button"
            onClick={(e) => {
                if (stopPropagation) e.stopPropagation();
                onClick();
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            aria-label={label}
            aria-haspopup="dialog"
            className={className}
            style={{
                /* 브라우저 기본 단추 모양을 전부 끈다 — 자리와 크기가 그대로여야 한다 */
                background: 'transparent',
                border: 'none',
                padding: 0,
                margin: 0,
                font: 'inherit',
                color: 'inherit',
                textAlign: 'inherit',
                cursor: 'pointer',
                ...(underline
                    ? {
                          textDecoration: 'underline',
                          textDecorationStyle: 'dotted' as const,
                          textDecorationThickness: 1,
                          textUnderlineOffset: 4,
                          textDecorationColor: 'currentColor',
                          opacity: hover ? 0.86 : 1,
                      }
                    : { opacity: hover ? 0.86 : 1 }),
                ...style,
            }}
        >
            {children}
        </button>
    );
};
